import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import ContextMenu from '../components/ContextMenu';

const TutorEnquiries = () => {
  const location = useLocation();
  const [enquiries, setEnquiries] = useState([]);
  const [metadata, setMetadata] = useState({ area: [], location: [] });
  const [filters, setFilters] = useState({ area: '', status: '' });
  const [showModal, setShowModal] = useState(false);
  const [editingEnquiry, setEditingEnquiry] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    gender: '',
    age: '',
    area: '',
    address: '',
    mobileNumbers: [''],
    educationalQualifications: '',
    fluencyInEnglish: 'Good',
    preferredTime: '',
    teachingExperience: '',
    preferredLocations: [''],
    preferredClasses: [''],
    preferredSubjects: [''],
    preferredBoards: [],
    ownVehicle: false,
    registrationFeesPaid: false,
    dateOfJoining: '',
    registrationFeeRenewalDate: '',
    kycDetails: {
      formSubmitted: false,
      passportPhoto: false,
      aadhaarFront: false,
      aadhaarBack: false
    },
    aadhaarCardGiven: false,
    rulesAcknowledged: false,
    rating: '',
    remarks: '',
    status: 'New'
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchEnquiries();
    fetchMetadata();
  }, [filters]);

  // Auto-open add modal if navigated from dashboard
  useEffect(() => {
    if (location.state?.openAddModal) {
      resetForm();
      setShowModal(true);
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });

      const response = await axios.get(`/api/tutor-enquiries?${params}`);
      setEnquiries(response.data.enquiries || []);
    } catch (error) {
      console.error('Error fetching enquiries:', error);
      setMessage('Error fetching enquiries');
    } finally {
      setLoading(false);
    }
  };

  const fetchMetadata = async () => {
    try {
      const response = await axios.get('/api/metadata');
      setMetadata(response.data);
    } catch (error) {
      console.error('Error fetching metadata:', error);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleArrayChange = (index, field, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({ ...formData, [field]: newArray });
  };

  const addArrayField = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ''] });
  };

  const removeArrayField = (index, field) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: newArray });
  };

  const handleBoardChange = (board) => {
    const boards = formData.preferredBoards.includes(board)
      ? formData.preferredBoards.filter(b => b !== board)
      : [...formData.preferredBoards, board];
    setFormData({ ...formData, preferredBoards: boards });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    // Filter out empty strings from arrays
    const cleanedData = {
      ...formData,
      mobileNumbers: formData.mobileNumbers.filter(m => m.trim()),
      preferredLocations: formData.preferredLocations.filter(l => l.trim()),
      preferredClasses: formData.preferredClasses.filter(c => c.trim()),
      preferredSubjects: formData.preferredSubjects.filter(s => s.trim())
    };

    try {
      if (editingEnquiry) {
        await axios.put(`/api/tutor-enquiries/${editingEnquiry._id}`, cleanedData);
        setMessage('Enquiry updated successfully!');
      } else {
        await axios.post('/api/tutor-enquiries', cleanedData);
        setMessage('Enquiry created successfully!');
      }

      resetForm();
      fetchEnquiries();
      setShowModal(false);
    } catch (error) {
      console.error('Error saving enquiry:', error);
      setMessage(error.response?.data?.message || 'Error saving enquiry');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (enquiry) => {
    setEditingEnquiry(enquiry);
    setFormData({
      ...enquiry,
      mobileNumbers: enquiry.mobileNumbers?.length ? enquiry.mobileNumbers : [''],
      preferredLocations: enquiry.preferredLocations?.length ? enquiry.preferredLocations : [''],
      preferredClasses: enquiry.preferredClasses?.length ? enquiry.preferredClasses : [''],
      preferredSubjects: enquiry.preferredSubjects?.length ? enquiry.preferredSubjects : [''],
      preferredBoards: enquiry.preferredBoards || [],
      dateOfJoining: enquiry.dateOfJoining?.split('T')[0] || '',
      registrationFeeRenewalDate: enquiry.registrationFeeRenewalDate?.split('T')[0] || '',
      kycDetails: enquiry.kycDetails || {
        formSubmitted: false,
        passportPhoto: false,
        aadhaarFront: false,
        aadhaarBack: false
      }
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this enquiry?')) return;

    try {
      await axios.delete(`/api/tutor-enquiries/${id}`);
      setMessage('Enquiry deleted successfully!');
      fetchEnquiries();
    } catch (error) {
      console.error('Error deleting enquiry:', error);
      setMessage('Error deleting enquiry');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      gender: '',
      age: '',
      area: '',
      address: '',
      mobileNumbers: [''],
      educationalQualifications: '',
      fluencyInEnglish: 'Good',
      preferredTime: '',
      teachingExperience: '',
      preferredLocations: [''],
      preferredClasses: [''],
      preferredSubjects: [''],
      preferredBoards: [],
      ownVehicle: false,
      registrationFeesPaid: false,
      dateOfJoining: '',
      registrationFeeRenewalDate: '',
      kycDetails: {
        formSubmitted: false,
        passportPhoto: false,
        aadhaarFront: false,
        aadhaarBack: false
      },
      aadhaarCardGiven: false,
      rulesAcknowledged: false,
      rating: '',
      remarks: '',
      status: 'New'
    });
    setEditingEnquiry(null);
  };

  return (
    <div className="container" style={{ padding: '20px' }}>
      <div className="page-header-compact">
        <h1>Tutor Enquiries</h1>
      </div>

      {message && (
        <div className={`alert ${message.includes('Error') ? 'alert-error' : 'alert-success'}`}>
          {message}
        </div>
      )}

      {/* Filters and Add Button */}
      <div className="filters-toolbar-collapsible">
        <div className="filters-toolbar-header">
          <button
            className="filters-toggle-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
              <path d="M3 7H21M3 12H15M3 17H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Filters
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              style={{
                marginLeft: '8px',
                transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }}
            >
              <path d="M7 10L12 15L17 10H7Z" fill="currentColor"/>
            </svg>
          </button>
          <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
            + Add New Tutor
          </button>
        </div>

        {showFilters && (
          <div className="filters-inline-collapsible">
            <div className="form-group">
              <label>Area</label>
              <select name="area" value={filters.area} onChange={handleFilterChange}>
                <option value="">All Areas</option>
                {metadata.area?.map((a) => (
                  <option key={a._id} value={a.value}>{a.value}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select name="status" value={filters.status} onChange={handleFilterChange}>
                <option value="">All Status</option>
                <option value="New">New</option>
                <option value="Verified">Verified</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="card-compact">
        <div className="card-header-compact">
          <h3>Tutors List</h3>
          <span className="record-count">{enquiries.length} records</span>
        </div>
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Gender</th>
                  <th>Age</th>
                  <th>Area</th>
                  <th>Mobile</th>
                  <th>Experience</th>
                  <th>Status</th>
                  <th>Created By</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {enquiries.length === 0 ? (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '40px' }}>
                      No tutors found
                    </td>
                  </tr>
                ) : (
                  enquiries.map((enquiry) => (
                    <tr key={enquiry._id}>
                      <td>{enquiry.name}</td>
                      <td>{enquiry.gender}</td>
                      <td>{enquiry.age}</td>
                      <td>{enquiry.area}</td>
                      <td>{enquiry.mobileNumbers?.[0]}</td>
                      <td>{enquiry.teachingExperience}</td>
                      <td>
                        <span className={`status-badge status-${enquiry.status?.toLowerCase()}`}>
                          {enquiry.status}
                        </span>
                      </td>
                      <td>{enquiry.createdByName || 'N/A'}</td>
                      <td style={{ padding: '16px', textAlign: 'right' }}>
                        <ContextMenu
                          onEdit={() => handleEdit(enquiry)}
                          onDelete={() => handleDelete(enquiry._id)}
                          itemName={enquiry.name}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-compact" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <h2>{editingEnquiry ? 'Edit' : 'Add'} Tutor Enquiry</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label>Name *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Gender</label>
                  <select name="gender" value={formData.gender} onChange={handleChange}>
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Age</label>
                  <input type="number" name="age" value={formData.age} onChange={handleChange} min="18" max="100" />
                </div>
                <div className="form-group">
                  <label>Area *</label>
                  <input type="text" name="area" value={formData.area} onChange={handleChange} list="area-list" required />
                  <datalist id="area-list">
                    {metadata.area?.map((a) => (<option key={a._id} value={a.value} />))}
                  </datalist>
                </div>
              </div>

              <div className="form-group">
                <label>Mobile Numbers *</label>
                {formData.mobileNumbers.map((mobile, index) => (
                  <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      value={mobile}
                      onChange={(e) => handleArrayChange(index, 'mobileNumbers', e.target.value)}
                      placeholder="Mobile number"
                      required={index === 0}
                    />
                    {index > 0 && (
                      <button type="button" className="btn btn-danger" onClick={() => removeArrayField(index, 'mobileNumbers')}>
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="btn btn-secondary" onClick={() => addArrayField('mobileNumbers')}>
                  + Add Mobile
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label>Educational Qualifications</label>
                  <textarea name="educationalQualifications" value={formData.educationalQualifications} onChange={handleChange} rows="2" />
                </div>
                <div className="form-group">
                  <label>Teaching Experience</label>
                  <textarea name="teachingExperience" value={formData.teachingExperience} onChange={handleChange} rows="2" />
                </div>
                <div className="form-group">
                  <label>Fluency in English</label>
                  <select name="fluencyInEnglish" value={formData.fluencyInEnglish} onChange={handleChange}>
                    <option value="Excellent">Excellent</option>
                    <option value="Good">Good</option>
                    <option value="Average">Average</option>
                    <option value="Poor">Poor</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Preferred Time</label>
                  <input type="text" name="preferredTime" value={formData.preferredTime} onChange={handleChange} />
                </div>
              </div>

              <div className="form-group">
                <label>Preferred Locations</label>
                {formData.preferredLocations.map((location, index) => (
                  <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      value={location}
                      onChange={(e) => handleArrayChange(index, 'preferredLocations', e.target.value)}
                      list="location-list"
                      placeholder="Preferred location"
                    />
                    <datalist id="location-list">
                      {metadata.location?.map((l) => (<option key={l._id} value={l.value} />))}
                    </datalist>
                    {index > 0 && (
                      <button type="button" className="btn btn-danger" onClick={() => removeArrayField(index, 'preferredLocations')}>
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="btn btn-secondary" onClick={() => addArrayField('preferredLocations')}>
                  + Add Location
                </button>
              </div>

              <div className="form-group">
                <label>Preferred Classes/Courses</label>
                {formData.preferredClasses.map((cls, index) => (
                  <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      value={cls}
                      onChange={(e) => handleArrayChange(index, 'preferredClasses', e.target.value)}
                      placeholder="Preferred class"
                    />
                    {index > 0 && (
                      <button type="button" className="btn btn-danger" onClick={() => removeArrayField(index, 'preferredClasses')}>
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="btn btn-secondary" onClick={() => addArrayField('preferredClasses')}>
                  + Add Class
                </button>
              </div>

              <div className="form-group">
                <label>Preferred Subjects</label>
                {formData.preferredSubjects.map((subject, index) => (
                  <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => handleArrayChange(index, 'preferredSubjects', e.target.value)}
                      placeholder="Preferred subject"
                    />
                    {index > 0 && (
                      <button type="button" className="btn btn-danger" onClick={() => removeArrayField(index, 'preferredSubjects')}>
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="btn btn-secondary" onClick={() => addArrayField('preferredSubjects')}>
                  + Add Subject
                </button>
              </div>

              <div className="form-group">
                <label>Preferred Board(s)</label>
                <div style={{ display: 'flex', gap: '16px' }}>
                  {['State', 'CBSE', 'ICSE', 'Custom'].map(board => (
                    <label key={board} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={formData.preferredBoards.includes(board)}
                        onChange={() => handleBoardChange(board)}
                      />
                      {board}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Address</label>
                <textarea name="address" value={formData.address} onChange={handleChange} rows="2" />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label>Date of Joining</label>
                  <input type="date" name="dateOfJoining" value={formData.dateOfJoining} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Registration Fee Renewal Date</label>
                  <input type="date" name="registrationFeeRenewalDate" value={formData.registrationFeeRenewalDate} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Rating</label>
                  <select name="rating" value={formData.rating} onChange={handleChange}>
                    <option value="">Select Rating</option>
                    <option value="Avg">Avg</option>
                    <option value="Regular">Regular</option>
                    <option value="Excellent">Excellent</option>
                    <option value="Highfees">Highfees</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select name="status" value={formData.status} onChange={handleChange}>
                    <option value="New">New</option>
                    <option value="Verified">Verified</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="checkbox" name="ownVehicle" checked={formData.ownVehicle} onChange={handleChange} />
                    Own Vehicle
                  </label>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="checkbox" name="registrationFeesPaid" checked={formData.registrationFeesPaid} onChange={handleChange} />
                    Registration Fees Paid
                  </label>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="checkbox" name="aadhaarCardGiven" checked={formData.aadhaarCardGiven} onChange={handleChange} />
                    AADHAAR Card Given (F&B)
                  </label>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="checkbox" name="rulesAcknowledged" checked={formData.rulesAcknowledged} onChange={handleChange} />
                    Rules & Regulations Acknowledged
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>KYC Details</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.kycDetails.formSubmitted}
                      onChange={(e) => setFormData({...formData, kycDetails: {...formData.kycDetails, formSubmitted: e.target.checked}})}
                    />
                    Form Submitted
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.kycDetails.passportPhoto}
                      onChange={(e) => setFormData({...formData, kycDetails: {...formData.kycDetails, passportPhoto: e.target.checked}})}
                    />
                    Passport Photo
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.kycDetails.aadhaarFront}
                      onChange={(e) => setFormData({...formData, kycDetails: {...formData.kycDetails, aadhaarFront: e.target.checked}})}
                    />
                    AADHAAR Front
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.kycDetails.aadhaarBack}
                      onChange={(e) => setFormData({...formData, kycDetails: {...formData.kycDetails, aadhaarBack: e.target.checked}})}
                    />
                    AADHAAR Back
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Remarks</label>
                <textarea name="remarks" value={formData.remarks} onChange={handleChange} rows="2" placeholder="Any additional notes" />
              </div>


              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TutorEnquiries;
